import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import Loader from '@atomaro/ui-kit/components/Loader/Loader';
import {useResize} from 'app/lib/hooks/useResize';
import {formatDate, formatDistanceToNowStrict} from 'date-fns';
import {ru} from 'date-fns/locale';
import {useDiaryStore} from 'entities/diary';
import {useDiaryTable} from 'features/diary/lib/useDiaryTable';
import {FC, useEffect, useState} from 'react';
import DataTable, {TableColumn} from 'react-data-table-component';
import {InView} from 'react-intersection-observer';
import {toRem} from 'shared/helpers';
import {DiaryNewEntrie} from '../diaryNewEntrie/DiaryNewEntrie';
import {DiaryAttachmentsCell} from './DiaryAttachmentsCell';
import {DiaryAuthorCell} from './DiaryAuthorCell';
import {DiaryDetail} from './DiaryDetail';
import {DiaryDictionariesCell} from './DiaryDictionariesCell';
import {DiaryEditCell} from './DiaryEditCell';
import {DiaryEditHeader} from './DiaryEditHeader';
import {DiaryMobileTable} from './DiaryMobileTable';
import {DiaryOldNewValue} from './DiaryOldNewValue';
import {TableWrapper} from './styles';
import {DiaryRow} from './types';

type DiaryTableProps = {
  onNewEntrieClick?: () => void;
  entryIdFromUrl?: string;
  onEntryOpen?: () => void;
};

export const DiaryTable: FC<DiaryTableProps> = ({
  onNewEntrieClick,
  entryIdFromUrl,
  onEntryOpen,
}) => {
  const {isScreenLg, isScreenMd} = useResize();
  const {
    rows,
    conditionalRowStyles,
    customStyles,
    isLoading,
    activeCols,
    colItems,
    editValues,
    isEditOpen,
    editAuthor,
    editDictionaries,
    editAttachments,
    editId,
    detailRow,
    isDetailOpen,
    userInfo,
    editEventValues,
    onRowClick,
    onActiveColsChange,
    onEditClick,
    setIsEditOpen,
    setIsDetailOpen,
    onInfiniteScroll,
    customFields,
    customFieldsCols,
  } = useDiaryTable();

  const entries = useDiaryStore((s) => s.entries);

  useEffect(() => {
    if (entryIdFromUrl && !isDetailOpen) {
      // Сначала ищем в rows (уже преобразованные записи)
      let foundRow = rows.find(
        (row) => row.id === entryIdFromUrl || String(row.id) === entryIdFromUrl
      );

      // Если не найдено в rows, ищем в entries и преобразуем в DiaryRow
      if (!foundRow && entries.length > 0) {
        const foundEntry = entries.find(
          (entry) => entry.id === entryIdFromUrl || String(entry.id) === entryIdFromUrl
        );
        if (foundEntry) {
          foundRow = {
            id: foundEntry.id,
            inspectedAt: new Date(foundEntry.inspectedAt),
            dateVisible: formatDate(foundEntry.inspectedAt, 'dd.MM.yyyy'),
            hiveNumber: foundEntry.hive.number,
            hiveId: foundEntry.hive.id,
            hive: foundEntry.hive,
            author: foundEntry.author,
            frames: foundEntry.frames,
            strength: foundEntry.strength,
            brood: foundEntry.brood,
            dictionaries: foundEntry.dictionaries || [],
            note: foundEntry.note?.trim?.() || '-',
            attachments: foundEntry.attachments || [],
            updatedAt: new Date(foundEntry.updatedAt),
            framesPrev: foundEntry.framesPrev,
            strengthPrev: foundEntry.strengthPrev,
            broodPrev: foundEntry.broodPrev,
            events: foundEntry.events || [],
          };
        }
      }

      if (foundRow) {
        onRowClick(foundRow);
        // Удаляем параметр из URL после открытия
        setTimeout(() => {
          onEntryOpen?.();
        }, 100);
      }
    }
  }, [entryIdFromUrl, rows, entries, onRowClick, onEntryOpen, isDetailOpen]);

  const allColumns: TableColumn<DiaryRow>[] = [
    {
      name: 'Дата осмотра',
      selector: (row) => formatDate(row.inspectedAt, 'dd.MM.yyyy'),
      width: toRem(130),
      sortable: true,
    },
    {
      name: 'Номер улья',
      selector: (row) => `№ ${row.hiveNumber}`,
      width: toRem(100),
      sortable: true,
    },
    {
      name: 'Сотрудник',
      selector: (row) => JSON.stringify(row.author),
      cell: (row) => <DiaryAuthorCell row={row} />,
      width: toRem(140),
      sortable: true,
    },
    {
      name: 'Рамки',
      selector: (row) => row.frames,
      cell: (row) => <DiaryOldNewValue oldValue={row.framesPrev} newValue={row.frames} />,
      width: toRem(100),
      sortable: true,
    },
    {
      name: 'Сила',
      selector: (row) => row.strength,
      cell: (row) => <DiaryOldNewValue oldValue={row.strengthPrev} newValue={row.strength} />,
      width: toRem(100),
      sortable: true,
    },
    {
      name: 'Расплод',
      selector: (row) => row.brood,
      cell: (row) => <DiaryOldNewValue oldValue={row.broodPrev} newValue={row.brood} />,
      width: toRem(100),
      sortable: true,
    },
    {
      name: 'Состояние',
      selector: (row) => row.dictionaries?.length || 0,
      cell: (row) => <DiaryDictionariesCell row={row} />,
      sortable: true,
    },
    {
      name: 'Комментарий',
      selector: (row) => row.note,
      sortable: true,
    },
    {
      name: 'Вложения',
      selector: (row) => JSON.stringify(row.attachments),
      cell: (row) => <DiaryAttachmentsCell row={row} />,
      width: toRem(132),
      sortable: true,
    },
    {
      name: 'Обновлено',
      selector: (row) => new Date(row.updatedAt).getTime(),
      cell: (row) =>
        formatDistanceToNowStrict(new Date(row.updatedAt), {
          locale: ru,
          addSuffix: true,
        }),
      width: toRem(140),
      sortable: true,
    },
    {
      name: (
        <DiaryEditHeader
          onChange={onActiveColsChange}
          items={colItems}
          customFields={customFieldsCols}
          randomRows={rows[0]}
        />
      ),
      cell: (row) =>
        (userInfo?.id === row?.author?.id || row?.hive.deletedAt !== null) && (
          <DiaryEditCell onClick={() => onEditClick(row)} />
        ),
      width: toRem(40),
      compact: true,
      sortable: true,
    },
  ];
  const [columns, setColumns] = useState<TableColumn<DiaryRow>[]>([]);

  const setCols = () => {
    const arr: TableColumn<DiaryRow>[] = [];
    if (!activeCols.length) {
      setColumns(allColumns);
      return;
    }

    for (let index = 0; index < activeCols.length; index++) {
      const col = activeCols[index];
      if (col.startsWith('field-')) {
        const fieldId = col.replace('field-', '');

        const def = customFields.find((f) => f.id === fieldId);
        if (!def) continue;

        arr.push({
          name: def.name,
          width: toRem(160),
          selector: (row) => {
            const f = row.fields?.find((cf) => cf.id === fieldId);
            if (!f) return '-';

            switch (def.type) {
              case 'NUMBER':
              case 'TEXT':
                return f.value ?? '-';
              case 'DATE':
                return f.value ? formatDate(f.value, 'dd.MM.yyyy') : '-';
              case 'DICTIONARY':
                //return f.dictionaries?.map((d) => d).join(", ") || "-";
                return f.dictionaries?.map((d) => d.name).join(', ') || '-';
              default:
                return '-';
            }
          },
        });

        continue;
      }
      switch (col) {
        case 'inspectedAt':
          arr.push({
            name: 'Дата осмотра',
            selector: (row) => formatDate(row.inspectedAt, 'dd.MM.yyyy'),
            width: toRem(130),
            sortable: true,
          });
          break;
        case 'hiveNumber':
          arr.push({
            name: 'Номер улья',
            selector: (row) => `№ ${row.hiveNumber}`,
            width: toRem(100),
            sortable: true,
          });
          break;
        case 'author':
          arr.push({
            name: 'Сотрудник',
            selector: (row) => JSON.stringify(row.author),
            cell: (row) => <DiaryAuthorCell row={row} />,
            width: toRem(140),
            sortable: true,
          });
          break;
        case 'frames':
          arr.push({
            name: 'Рамки',
            selector: (row) => JSON.stringify(row.frames),
            cell: (row) => <DiaryOldNewValue oldValue={row.framesPrev} newValue={row.frames} />,
            width: toRem(100),
            sortable: true,
          });
          break;
        case 'strength':
          arr.push({
            name: 'Сила',
            selector: (row) => JSON.stringify(row.strength),
            cell: (row) => <DiaryOldNewValue oldValue={row.strengthPrev} newValue={row.strength} />,
            width: toRem(100),
            sortable: true,
          });
          break;
        case 'brood':
          arr.push({
            name: 'Расплод',
            selector: (row) => JSON.stringify(row.brood),
            cell: (row) => <DiaryOldNewValue oldValue={row.broodPrev} newValue={row.brood} />,
            width: toRem(100),
            sortable: true,
          });
          break;
        case 'dictionaries':
          arr.push({
            name: 'Состояние',
            selector: (row) => row.dictionaries?.length || 0,
            cell: (row) => <DiaryDictionariesCell row={row} />,
            sortable: true,
          });
          break;
        case 'note':
          arr.push({
            name: 'Комментарий',
            selector: (row) => row.note,
            sortable: true,
          });
          break;
        case 'attachments':
          arr.push({
            name: 'Вложения',
            selector: (row) => row.attachments?.length ?? 0,
            cell: (row) => <DiaryAttachmentsCell row={row} />,
            width: toRem(132),
            sortable: true,
          });
          break;
        case 'updatedAt':
          arr.push({
            name: 'Обновлено',
            selector: (row) => new Date(row.updatedAt).getTime(),
            cell: (row) => {
              return formatDistanceToNowStrict(new Date(row.updatedAt), {
                locale: ru,
                addSuffix: true,
              });
            },
            width: toRem(140),
            sortable: true,
          });
          break;
        default:
          break;
      }
    }

    arr.push({
      name: (
        <DiaryEditHeader
          onChange={onActiveColsChange}
          items={colItems}
          customFields={customFieldsCols}
          randomRows={rows[0]}
        />
      ),
      cell: (row) =>
        (userInfo?.id === row?.author?.id || row?.hive.deletedAt !== null) && (
          <DiaryEditCell onClick={() => onEditClick(row)} />
        ),
      width: toRem(40),
      compact: true,
    });

    setColumns(arr);
  };

  useEffect(() => {
    setCols();
  }, [activeCols]);

  const activeDateFilter = useDiaryStore((s) => s.activeDateFilter);
  return (
    <TableWrapper>
      {isScreenLg ? (
        <DataTable
          columns={columns}
          data={rows}
          noDataComponent={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '100px',
              }}
            >
              {!isLoading && !activeDateFilter && (
                <>
                  <p>В журнале пока нет ни одной записи</p>
                  <Button label="Добавить запись" onClick={onNewEntrieClick} />
                </>
              )}
              {activeDateFilter && <p>Не найдено записей, соответствующих фильтрам. </p>}
            </div>
          }
          conditionalRowStyles={conditionalRowStyles}
          customStyles={customStyles}
          onRowClicked={onRowClick}
          progressPending={isLoading && Boolean(rows.length)}
          progressComponent={<Loader size="m" variant="primary" />}
        />
      ) : (
        <DiaryMobileTable rows={rows} onRowClick={onRowClick} />
      )}
      <InView as="div" style={{padding: '2px'}} onChange={onInfiniteScroll} />
      {editValues && (
        <DiaryNewEntrie
          key={editId ?? 'now'}
          drawerProps={{
            isOpen: isEditOpen,
            position: 'right',
            onClose: () => setIsEditOpen(false),
          }}
          defaultValues={editValues}
          defaultAuthor={editAuthor}
          defaultDictionaries={editDictionaries}
          defaultAttachments={editAttachments}
          editId={editId}
          isMobile={!isScreenMd}
          editEventValues={editEventValues}
        />
      )}
      <DiaryDetail
        isOpen={isDetailOpen}
        row={detailRow}
        onClose={() => setIsDetailOpen(false)}
        onEdit={onEditClick}
        isMobile={!isScreenMd}
        showFooter={detailRow?.author?.id === userInfo?.id}
      />
    </TableWrapper>
  );
};
